/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { getCCMServices } from './feature'

import type { AnyCCMService, AnyCluster, CCMService } from './types'

/**
 * This type may be redundant when compared to CCMService, but this is the API type, which may lack non-supported
 * services but that we want to show in the UI to gauge users' interest in them.
 */
type ServiceName = keyof AnyCCMService

export function getCCMService(serviceName: ServiceName): CCMService {
  const services = getCCMServices()

  if (services.includes(serviceName as CCMService)) {
    return serviceName as CCMService
  }

  return 'auto_ops'
}

/**
 * Returns the enabled services for a cluster.
 */
export function getServicesForCluster(cluster: AnyCluster): CCMService[] {
  return getCCMServices().filter((service) => cluster.services[service].enabled)
}

/**
 * Builds a unique cluster name by combining the cluster name (if available) with a portion of the cluster ID.
 */
export function buildClusterName(cluster: {
  name?: string
  self_managed_cluster: { id: string }
}): string {
  return cluster.name
    ? `${cluster.name}+${cluster.self_managed_cluster.id.slice(0, 5)}`
    : cluster.self_managed_cluster.id
}

export function isCCMService(service: string): service is CCMService {
  return getCCMServices().includes(service as CCMService)
}
